import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router";
import { navigation } from "@/data/navigation";
import { retreats } from "@/data/content";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [retreatsOpen, setRetreatsOpen] = useState(false);

  // Close menu for navigation — clears body-lock styles so the
  // effect cleanup doesn't restore the old scroll position.
  const closeMenuForNavigation = () => {
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.overflow = "";
    setMenuOpen(false);
    setRetreatsOpen(false);
  };

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 60);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      // iOS Safari ignores overflow:hidden on body alone — pin the body with
      // position:fixed and preserve the scroll offset so the page doesn't jump.
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      // Restore scroll position
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      if (scrollY) window.scrollTo(0, parseInt(scrollY) * -1);
    };
  }, [menuOpen]);

  return (
    <>
    <header
      style={{ top: "var(--dev-banner-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-parchment/95 backdrop-blur-sm border-b border-warm-stone/20 py-3"
          : "bg-transparent py-6"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center px-6 lg:px-12">
        {/* Desktop Nav — centered */}
        <nav className="hidden items-center gap-10 md:flex" aria-label="Main navigation">
          {navigation.map((item) =>
            item.label === "Retreats" ? (
              <div key={item.path} className="group relative">
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `relative font-body text-xs tracking-[0.2em] uppercase transition-colors duration-300 hover:text-faded-terracotta after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-faded-terracotta after:transition-all after:duration-500 ${
                      isActive
                        ? "text-faded-terracotta after:w-full"
                        : "text-soft-charcoal/80 after:w-0 hover:after:w-full"
                    }`
                  }
                >
                  {item.label}
                </NavLink>

                {/* Dropdown */}
                <div className="pointer-events-none absolute top-full left-0 z-50 w-64 pt-4 opacity-0 transition-opacity duration-300 group-hover:pointer-events-auto group-hover:opacity-100">
                  <div className="rounded border border-warm-stone/25 bg-white px-5 py-5 shadow-xl">
                    <div className="space-y-4">
                      {retreats.map((retreat) =>
                        retreat.status === "upcoming" ? (
                          <Link
                            key={retreat.slug}
                            to={`/retreats/${retreat.slug}`}
                            className="group/item block transition-colors duration-200"
                          >
                            <p className="font-serif text-[15px] text-soft-charcoal transition-colors duration-200 group-hover/item:text-faded-terracotta">
                              {retreat.title}
                            </p>
                            <p className="mt-0.5 text-[10px] tracking-[0.15em] uppercase text-soft-charcoal/50">
                              {retreat.season} · {retreat.region}
                            </p>
                          </Link>
                        ) : (
                          <div key={retreat.slug}>
                            <p className="font-serif text-[15px] text-soft-charcoal/40">
                              {retreat.title}
                            </p>
                            <p className="mt-0.5 text-[10px] tracking-[0.15em] uppercase text-soft-charcoal/30">
                              {retreat.season} · {retreat.region}
                              <span className="ml-2 font-script text-base normal-case text-faded-terracotta/60">
                                Coming Soon
                              </span>
                            </p>
                          </div>
                        )
                      )}
                    </div>
                    <div className="mt-4 border-t border-warm-stone/20 pt-3">
                      <Link
                        to="/retreats"
                        className="block text-center text-[10px] tracking-[0.2em] uppercase text-soft-charcoal/50 transition-colors duration-200 hover:text-faded-terracotta"
                      >
                        View All Retreats
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `relative font-body text-xs tracking-[0.2em] uppercase transition-colors duration-300 hover:text-faded-terracotta after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-faded-terracotta after:transition-all after:duration-500 ${
                    isActive
                      ? "text-faded-terracotta after:w-full"
                      : "text-soft-charcoal/80 after:w-0 hover:after:w-full"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        {/* Mobile Hamburger — right-aligned */}
        <button
          className="relative ml-auto flex h-8 w-8 flex-col items-center justify-center gap-1.5 md:hidden"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          <span
            className={`block h-px w-6 bg-soft-charcoal transition-all duration-300 ${
              menuOpen ? "translate-y-[7px] rotate-45" : ""
            }`}
          />
          <span
            className={`block h-px w-6 bg-soft-charcoal transition-all duration-300 ${
              menuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block h-px w-6 bg-soft-charcoal transition-all duration-300 ${
              menuOpen ? "-translate-y-[7px] -rotate-45" : ""
            }`}
          />
        </button>
      </div>

    </header>

      {/* Mobile Overlay — outside header so backdrop-blur can't create a containing block */}
      <div
        className={`fixed inset-0 z-40 flex flex-col items-center justify-center bg-parchment transition-opacity duration-500 md:hidden ${
          menuOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <nav className="flex flex-col items-center gap-8" aria-label="Mobile navigation">
          {navigation.map((item) =>
            item.label === "Retreats" ? (
              <div key={item.path} className="flex flex-col items-center">
                <button
                  className={`font-serif text-2xl tracking-wide transition-colors hover:text-faded-terracotta ${
                    retreatsOpen ? "text-faded-terracotta" : "text-soft-charcoal"
                  }`}
                  onClick={() => setRetreatsOpen(!retreatsOpen)}
                >
                  {item.label}
                </button>

                {retreatsOpen && (
                  <div className="mt-4 flex flex-col items-center gap-3">
                    {retreats.map((retreat) =>
                      retreat.status === "upcoming" ? (
                        <NavLink
                          key={retreat.slug}
                          to={`/retreats/${retreat.slug}`}
                          className="text-center font-serif text-lg text-soft-charcoal/80 transition-colors hover:text-faded-terracotta"
                          onClick={closeMenuForNavigation}
                        >
                          {retreat.title}
                        </NavLink>
                      ) : (
                        <span
                          key={retreat.slug}
                          className="text-center font-serif text-lg text-soft-charcoal/40"
                        >
                          {retreat.title}
                          <span className="ml-2 font-script text-base text-faded-terracotta/50">
                            Coming Soon
                          </span>
                        </span>
                      )
                    )}
                    <NavLink
                      to="/retreats"
                      className="mt-1 text-xs tracking-[0.2em] uppercase text-warm-stone transition-colors hover:text-faded-terracotta"
                      onClick={closeMenuForNavigation}
                    >
                      View All Retreats
                    </NavLink>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `font-serif text-2xl tracking-wide transition-colors hover:text-faded-terracotta ${
                    isActive ? "text-faded-terracotta" : "text-soft-charcoal"
                  }`
                }
                onClick={closeMenuForNavigation}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>
      </div>
    </>
  );
}
